<template>
  <div class="create-listing-page">
    <v-container class="create-listing py-4">
      <!-- Header Section -->
      <v-row>
        <v-col cols="12">
          <div class="d-flex">
            <v-btn @click="$router.replace('/home')" class="back-button mr-3" icon text>
              <v-icon color="primary">mdi-arrow-left</v-icon>
            </v-btn>
            <h1 class="text-h5 font-weight-medium primary--text">Create New Listing</h1>
          </div>
          <v-divider class="mt-3 mb-4"></v-divider>
        </v-col>
      </v-row>

      <!-- Stepper for Create Listing Flow -->
      <v-row>
        <v-col cols="12">
          <v-stepper v-model="step" class="elevation-0">
            <v-stepper-header class="elevation-1 rounded-lg mb-4">
              <v-stepper-step :complete="step > 1" step="1" color="primary">
                Upload Image
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step :complete="step > 2" step="2" color="primary">
                Analysis
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step step="3" color="primary">
                Discogs Match
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </v-col>
      </v-row>

      <!-- Step 1: Image Upload Section -->
      <v-slide-y-transition>
        <v-row v-if="step === 1">
          <v-col cols="12">
            <v-card class="mx-auto upload-card" elevation="3" rounded="lg">
              <v-card-text class="px-4 pt-5 pb-5">
                <div class="text-center">
                  <v-avatar color="primary" size="32" class="mb-2 white--text elevation-1">
                    <v-icon size="18">mdi-image-plus</v-icon>
                  </v-avatar>
                  <h2 class="text-body-1 font-weight-medium">Upload Product Image</h2>
                  <p class="text-caption text-medium-emphasis my-1">Upload a high-quality image of your product</p>
                </div>
                <v-divider class="my-3"></v-divider>
                <v-row>
                  <v-col cols="12" sm="8" md="7" class="mx-auto">
                    <!-- Image Upload Area -->
                    <div class="upload-area" :class="{ 'has-image': imagePreview }">
                      <v-fade-transition mode="out-in">
                        <template v-if="!imagePreview">
                          <div class="d-flex flex-column align-center justify-center py-12">
                            <v-btn color="light-blue lighten-3" class="browse-btn mb-4" elevation="0" height="40"
                              min-width="160" @click="triggerFileInput">
                              <v-icon left size="18" class="mr-1">mdi-camera</v-icon>
                              <span class="text-subtitle-2 font-weight-medium">SELECT IMAGE</span>
                            </v-btn>
                            <p class="text-caption text-center text-medium-emphasis">Click to browse your files</p>
                            <v-file-input v-model="imageFile" accept="image/*" class="d-none" ref="fileInput"
                              @change="handleImageUpload"></v-file-input>
                          </div>
                        </template>
                        <template v-else>
                          <div class="image-preview-container">
                            <v-card flat class="mx-auto image-card" max-width="400">
                              <v-img :src="imagePreview" max-height="300" contain class="mx-auto"></v-img>
                            </v-card>
                            <div class="image-actions mt-4">
                              <v-btn color="error" class="mr-2" small @click="imageFile = null; imagePreview = null;"
                                outlined>
                                <v-icon left size="16">mdi-delete</v-icon>
                                Remove
                              </v-btn>
                              <v-btn color="primary" small @click="proceedToAnalysis" :loading="analyzing"
                                :disabled="analyzing">
                                <v-icon left size="16">mdi-arrow-right</v-icon>
                                Continue
                              </v-btn>
                            </div>
                          </div>
                        </template>
                      </v-fade-transition>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-slide-y-transition>

      <!-- Step 2: AI Analysis Results -->
      <v-slide-y-transition>
        <v-row v-if="step === 2" class="mt-4">
          <v-col cols="12">
            <v-card class="mx-auto analysis-results pt-6" elevation="6" rounded="xl">
              <div class="analysis-header pa-4 text-center">
                <v-avatar color="success" size="56" class="mt-n8 mb-3 white--text elevation-3">
                  <v-icon size="32" class="pt-1">mdi-check-decagram</v-icon>
                </v-avatar>
                <h2 class="text-h5 font-weight-bold">AI Analysis Results</h2>
                <p class="text-subtitle-1 text-medium-emphasis mt-1 mb-3">Here's what we found in your image</p>
                <v-divider></v-divider>
              </div>
              <v-card-text class="pa-6">
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="pb-4">
                      <div class="d-flex align-center mb-3">
                        <v-avatar color="light-blue lighten-5" size="36" class="mr-3">
                          <v-icon color="light-blue" size="20">mdi-package-variant</v-icon>
                        </v-avatar>
                        <h3 class="text-subtitle-1 font-weight-bold primary--text mb-0">Product Details</h3>
                      </div>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-tag</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Product Type</div>
                            <div class="text-subtitle-1 font-weight-medium">
                              {{ analysisResults.productType || 'Not detected'
                              }}</div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-account-music</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Artist</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.artist || 'Not detected'
                              }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-album</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Title</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.title || 'Not detected'
                              }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-star-check</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Condition</div>
                            <div class="text-subtitle-1 font-weight-medium">
                              {{ analysisResults.condition || 'Not detected' }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-map-marker</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Country</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.country || 'Not detected'
                              }}
                            </div>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="pb-4">
                      <div class="d-flex align-center mb-3">
                        <v-avatar color="light-blue lighten-5" size="36" class="mr-3">
                          <v-icon color="light-blue" size="20">mdi-information</v-icon>
                        </v-avatar>
                        <h3 class="text-subtitle-1 font-weight-bold primary--text mb-0">Media Information</h3>
                      </div>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-disc</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Format</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.format || 'Not detected'
                              }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-calendar</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Year/Release Date</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.year || 'Not detected' }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-label</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Label</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.label || 'Not detected'
                              }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-music</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Genre</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.genre || 'Not detected'
                              }}
                            </div>
                          </div>
                        </div>
                      </v-card>

                      <v-card flat outlined class="analysis-card mb-3 px-4 pt-3 pb-2">
                        <div class="d-flex align-start mb-1">
                          <v-avatar color="light-blue lighten-5" size="40" class="mr-3 mt-1">
                            <v-icon color="light-blue" size="20">mdi-emoticon</v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 text-medium-emphasis">Mood</div>
                            <div class="text-subtitle-1 font-weight-medium">{{ analysisResults.mood || 'Not detected' }}
                            </div>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </v-col>
                </v-row>

                <!-- Continue to Discogs button -->
                <v-card-actions class="justify-center pb-6 pt-2">
                  <v-btn color="primary" large rounded elevation="2" @click="proceedToDiscogs" :loading="loading">
                    <v-icon left>mdi-web</v-icon>
                    Continue to Discogs Matching
                  </v-btn>
                </v-card-actions>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-slide-y-transition>

      <!-- Step 3: Discogs Suggestions -->
      <v-slide-y-transition>
        <v-row v-if="step === 3" class="mt-4">
          <v-col cols="12">
            <v-card class="mx-auto discogs-card pt-6" elevation="6" rounded="xl">
              <div class="discogs-header pa-4 text-center">
                <v-avatar color="primary" size="56" class="mt-n8 mb-2 white--text elevation-3">
                  <v-icon size="32">mdi-web</v-icon>
                </v-avatar>
                <h2 class="text-h5 font-weight-bold">Discogs Suggestions</h2>
                <p class="text-subtitle-1 text-medium-emphasis mt-1 mb-3">Select a matching record from Discogs</p>
                <v-divider></v-divider>
              </div>
              <v-card-text class="pa-6">
                <!-- Search field for Discogs results -->
                <v-card flat class="mb-4 px-2">
                  <v-card-text class="py-2">
                    <v-row>
                      <v-col cols="12">
                        <v-text-field v-model="discogsSearchQuery" label="Search by title"
                          placeholder="Type to filter results by title..." outlined dense clearable
                          prepend-inner-icon="mdi-magnify" hide-details="auto"></v-text-field>
                      </v-col>
                    </v-row>
                    <div class="caption grey--text text--darken-1 mt-1"
                      v-if="filteredDiscogsSuggestions.length && filteredDiscogsSuggestions.length < discogsSuggestions.length">
                      Showing {{ filteredDiscogsSuggestions.length }} of {{ discogsSuggestions.length }} matching titles
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Desktop view (md and up) -->
                <v-data-table :headers="headers" :items="filteredDiscogsSuggestions" :items-per-page="5"
                  class="elevation-2 discogs-table rounded-lg hidden-sm-and-down" :loading="loading"
                  loading-text="Loading Discogs results..." no-data-text="No matching records found in Discogs"
                  @click:row="goToRelease" :footer-props="{
                    'items-per-page-options': [5, 10, 15],
                    'show-current-page': true,
                    'show-first-last-page': true
                  }" style="cursor: pointer">
                  <template v-slot:item.cover_image="{ item }">
                    <div class="d-flex justify-center">
                      <v-avatar size="60" rounded class="elevation-3 album-cover d-flex align-center justify-center">
                        <v-img :src="item.cover_image" max-height="54" max-width="54" contain class="mx-auto"></v-img>
                      </v-avatar>
                    </div>
                  </template>
                  <template v-slot:item.title="{ item }">
                    <div class="font-weight-medium">{{ item.title }}</div>
                  </template>
                  <template v-slot:item.year="{ item }">
                    <v-chip x-small outlined color="primary" v-if="item.year">{{ item.year }}</v-chip>
                    <span v-else>-</span>
                  </template>
                </v-data-table>

                <!-- Mobile view (xs and sm) -->
                <div class="hidden-md-and-up">
                  <template v-if="loading">
                    <v-skeleton-loader v-for="i in 3" :key="`skeleton-${i}`" type="list-item-avatar-three-line"
                      class="mb-4"></v-skeleton-loader>
                  </template>

                  <div v-else-if="filteredDiscogsSuggestions.length === 0" class="text-center pa-4">
                    <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-album-off</v-icon>
                    <div class="text-subtitle-1 grey--text">No matching records found in Discogs</div>
                  </div>

                  <v-card v-else v-for="(item, index) in filteredDiscogsSuggestions" :key="`result-${item.id || index}`"
                    class="mb-4 discogs-mobile-card elevation-2" @click="goToRelease(item)" ripple>
                    <div class="d-flex">
                      <div class="mobile-album-cover-wrapper">
                        <div class="mobile-album-cover-inner">
                          <v-img :src="item.cover_image" class="mobile-album-cover" contain></v-img>
                          <div class="album-overlay">
                            <v-icon color="white" size="20">mdi-import</v-icon>
                          </div>
                        </div>
                      </div>
                      <div class="mobile-content-wrapper">
                        <!-- Artist (if available) -->
                        <div v-if="item.artist" class="artist-name">
                          {{ item.artist }}
                        </div>
                        <div v-else-if="item.artists && item.artists.length > 0" class="artist-name">
                          {{ typeof item.artists[0] === 'object' ? item.artists[0].name : item.artists[0] }}
                        </div>

                        <!-- Title without truncation -->
                        <div class="album-title">{{ item.title }}</div>

                        <!-- Year and Format row -->
                        <div class="album-details">
                          <v-chip x-small outlined color="primary" class="year-chip" v-if="item.year">{{ item.year
                            }}</v-chip>
                          <div class="format-text">
                            {{ typeof item.format === 'string' ? item.format : (Array.isArray(item.format) ?
                              item.format.join(', ') : '') }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-card>
                </div>

                <!-- Continue without Discogs button -->
                <v-card-actions class="justify-center pb-6 pt-4">
                  <v-btn color="secondary" large rounded elevation="2" @click="continueWithoutDiscogs">
                    <v-icon left>mdi-arrow-right</v-icon>
                    Continue without Discogs
                  </v-btn>
                </v-card-actions>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-slide-y-transition>


      <v-slide-y-transition>
        <v-row v-if="analysisResults" class="mt-12 mb-8">
          <v-col cols="12" class="text-center">
            <div class="continue-action-wrapper d-inline-block">
            </div>
          </v-col>
        </v-row>
      </v-slide-y-transition>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'CreateListing',
  data() {
    return {
      step: 1,
      imageFile: null,
      imagePreview: null,
      analyzing: false,
      analysisResults: null,
      loading: false,
      discogsSuggestions: [],
      discogsSearchQuery: '',
      filteredDiscogsSuggestions: [],
      headers: [
        { text: 'Cover', value: 'cover_image', sortable: false, align: 'center', width: '100px' },
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Year', value: 'year', sortable: true, width: '80px' },
        { text: 'Type', value: 'type', sortable: true, width: '100px' },
        { text: 'Format', value: 'format', sortable: true },
        { text: 'Label', value: 'label', sortable: true },
        { text: 'Cat No', value: 'catno', sortable: true, width: '100px' }
      ]
    };
  },
  created() {
    // Reset any previously stored image or analysis data
    this.imageFile = null;
    this.imagePreview = null;
    this.analysisResults = null;
    this.discogsSuggestions = [];
    this.step = 1;
  },
  watch: {
    discogsSearchQuery: {
      handler(newQuery) {
        // If empty query, show all results
        if (newQuery.trim() === '') {
          this.filteredDiscogsSuggestions = this.discogsSuggestions;
          return;
        }

        // Filter results based on title match only
        const query = newQuery.toLowerCase().trim();
        const filtered = this.discogsSuggestions.filter(item => {
          return item.title && item.title.toLowerCase().includes(query);
        });

        this.filteredDiscogsSuggestions = filtered;
      },
      immediate: true
    }
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.$refs.input.click();
    },
    handleImageUpload(file) {
      if (!file) {
        this.imagePreview = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    // Stepper navigation methods
    proceedToAnalysis() {
      if (!this.imageFile) return;

      this.analyzing = true;
      this.analyzeImage().then(() => {
        this.step = 2;
        // Add scroll to top when transitioning to step 2
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }).finally(() => {
        this.analyzing = false;
      });
    },

    proceedToDiscogs() {
      this.loading = true;
      this.searchDiscogs().then(() => {
        this.step = 3;
        // Add scroll to top when transitioning to step 3
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }).finally(() => {
        this.loading = false;
      });
    },

    continueWithoutDiscogs() {
      // Scroll to top before navigation
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      // Navigate to Release component with just the analysis data
      this.$router.push({
        name: 'Release',
        params: { mode: 'new', id: 'new' },
        query: {
          analysis: JSON.stringify(this.analysisResults)
        }
      });
    },

    async analyzeImage() {
      if (!this.imageFile) return Promise.reject('No image file');

      try {
        const formData = new FormData();
        formData.append('image', this.imageFile);

        // Call the AI analysis endpoint
        const response = await fetch(`${process.env.VUE_APP_ENDPOINT}/api/analyze-product-image`, {
          method: 'POST',
          body: formData
        });

        const data = await response.json();
        // Ensure all keys are present, even if blank
        const blankAnalysis = {
          productType: '', artist: '', title: '', condition: '', format: '', year: '', label: '', country: '', genre: '', mood: ''
        };
        this.analysisResults = { ...blankAnalysis, ...(data.analysis || {}) };
        return Promise.resolve(this.analysisResults);
      } catch (error) {
        console.error('Error analyzing image:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to analyze image. Please try again.'
        });
        return Promise.reject(error);
      }
    },

    async searchDiscogs() {
      if (!this.analysisResults) return Promise.reject('No analysis results');

      try {
        // Use artist and title for Discogs search
        const query = `${this.analysisResults.title} ${this.analysisResults.artist} ${this.analysisResults.format}`.trim();
        const response = await fetch(
          `${process.env.VUE_APP_ENDPOINT}/api/discogs/search?q=${encodeURIComponent(query)}&type=release`
        );
        const data = await response.json();
        this.discogsSuggestions = data.results || [];
        this.filteredDiscogsSuggestions = this.discogsSuggestions;
        return Promise.resolve(this.discogsSuggestions);
      } catch (error) {
        console.error('Error searching Discogs:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to search Discogs. Please try again.'
        });
        return Promise.reject(error);
      }
    },

    async goToRelease(item) {
      // Scroll to top before fetching and navigation
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      // Fetch full release info from Discogs via proxy
      try {
        const response = await fetch(`${process.env.VUE_APP_ENDPOINT}/api/discogs/release/${item.id}`);
        const releaseData = await response.json();

        // Map Discogs data to Release.vue fields
        const mapped = {
          title: releaseData.title || '',
          artist: releaseData.artists?.[0]?.name || '',
          year: releaseData.year || '',
          label: releaseData.labels?.[0]?.name || '',
          format: releaseData.formats?.[0]?.name || '',
          country: releaseData.country || '',
          genre: releaseData.genres?.[0] || '',
          mood: '', // Discogs doesn't provide mood
          condition: 'Used', // Default to Used
          productType: (releaseData.formats?.[0]?.name || '').toLowerCase(),
          discogsuri: releaseData.images?.[0]?.uri || '',
          lowest_price: releaseData.lowest_price || '',
          want: releaseData.community?.want || '',
          have: releaseData.community?.have || '',
          catlogNo: releaseData.labels?.[0]?.catno || '',
          album: releaseData.title || '',
          tracklist: releaseData.tracklist?.map(e => ({
            position: e.position,
            title: e.title,
            duration: e.duration,
          })) || [],
          recordCondition: 'M', // Default to Mint
          coverCondition: 'M', // Default to Mint
          language: 'English', // Default to English
          weight: '500', // Default weight for vinyl
          listPrice: 1499, // Default list price
          sellingPrice: 1499, // Default selling price
          buyingPrice: 500, // Default buying price
          inventoryLocation: "A", // Default inventory location
          customCategory: [], // Empty custom category
          keywords: [], // Empty keywords
          noofrec: 1, // Default number of records
          speed: '33', // Default speed
          dimensions: {
            length: 35,
            width: 35,
            height: 5
          }
        };

        // Set weight based on product type
        if (mapped.productType === 'cd') {
          mapped.weight = '250';
        } else if (mapped.productType === 'cassette') {
          mapped.weight = '150';
        }

        // Navigate to Release component with the mapped data
        this.$router.push({
          name: 'Release',
          params: { mode: 'new', id: 'new' },
          query: {
            analysis: JSON.stringify(mapped)
          }
        });
      } catch (error) {
        console.error('Error fetching Discogs release:', error);
        this.$swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch Discogs release details.'
        });
      }
    },

    continueToDetails() {
      // Navigate to Release component with the analyzed data without image
      this.$router.push({
        name: 'Release',
        params: { mode: 'new', id: 'new' },
        query: {
          analysis: JSON.stringify(this.analysisResults)
        }
      });
    }
  }
};
</script>

<style scoped>
.create-listing-page {
  background-color: #f8f9fa;
  min-height: 100vh;
  animation: fadeIn 0.6s ease-in-out;
}

.create-listing {
  max-width: 1200px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}

.v-card {
  transition: all 0.3s ease;
  overflow: hidden;
  margin-bottom: 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.07) !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.upload-card-header,
.analysis-header,
.discogs-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.back-button {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
}

.back-button:hover {
  transform: translateX(-5px);
}

.upload-area {
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  transition: all 0.3s ease;
  min-height: 240px;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0 auto;
  padding: 15px;
  overflow: hidden;
}

.upload-area:hover {
  border-color: var(--v-primary-base);
  background-color: rgba(0, 0, 0, 0.03);
}

.upload-area.has-image {
  border-style: solid;
  border-color: var(--v-primary-base);
  background-color: white;
}

.upload-placeholder {
  width: 100%;
  height: 100%;
  min-height: 280px;
}

.pulse-icon {
  animation: pulse 2s infinite;
}

.image-preview-wrapper {
  position: relative;
  display: inline-block;
}

.image-preview {
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
  width: auto;
  height: auto;
  max-width: 70%;
  max-height: 200px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.image-preview-container {
  width: 100%;
  text-align: center;
  margin-bottom: 16px;
}

.image-wrapper {
  display: inline-block;
  max-width: 90%;
  margin: 0 auto 15px;
}

.image-remove-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: white !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.image-remove-btn:hover {
  transform: scale(1.1);
}

.browse-btn {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  min-width: 180px;
  color: rgba(0, 0, 0, 0.8) !important;
  font-weight: 500;
}

.browse-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.action-button {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  min-width: 180px;
}

.action-button:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 7px 15px rgba(0, 0, 0, 0.25) !important;
}

.analysis-card {
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.09) !important;
}

.analysis-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 0, 0, 0.14) !important;
}

.discogs-table {
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff !important;
}

.discogs-table>>>tbody tr {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.discogs-table>>>tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

.album-cover {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.album-cover:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15) !important;
}

.album-cover .v-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-album-cover-wrapper {
  width: 100px;
  min-width: 100px;
  flex-shrink: 0;
  background-color: #f0f0f0;
  position: relative;
  overflow: hidden;
}

.mobile-album-cover-inner {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.mobile-album-cover {
  width: 84px;
  height: 84px;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  background-color: white;
  position: relative;
  z-index: 1;
}

.album-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
  border-radius: 6px;
}

.mobile-album-cover-inner:hover .album-overlay {
  opacity: 1;
}

.mobile-content-wrapper {
  flex-grow: 1;
  min-width: 0;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.discogs-mobile-card {
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  border-radius: 10px;
  border: none !important;
}

.discogs-mobile-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15) !important;
}

.discogs-mobile-card:active {
  transform: translateY(0px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
}

.artist-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v-primary-base);
  margin-bottom: 4px;
  line-height: 1.2;
}

.album-title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.87);
}

.album-details {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.year-chip {
  height: 20px !important;
  font-size: 0.7rem !important;
}

.format-text {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.continue-btn {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;
}

.continue-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.4) !important;
}

/* Mobile responsiveness improvements */
@media (max-width: 600px) {
  .create-listing {
    padding: 12px !important;
  }

  .upload-area {
    padding: 16px !important;
    min-height: 240px;
  }

  .image-preview {
    max-width: 90% !important;
  }

  .upload-placeholder {
    min-height: 200px;
  }

  .discogs-card {
    border-radius: 12px !important;
  }

  .discogs-header {
    padding: 16px 12px !important;
  }

  .discogs-header h2 {
    font-size: 1.25rem !important;
  }

  .discogs-header p {
    font-size: 0.875rem !important;
  }

  .v-card-text.pa-6 {
    padding: 16px 12px !important;
  }

  .mobile-content-wrapper {
    min-width: 0;
    width: calc(100% - 100px);
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
  }

  .artist-name,
  .album-title {
    white-space: normal;
    overflow: visible;
  }

  .discogs-mobile-card {
    margin-bottom: 12px !important;
  }

  .mobile-album-cover-wrapper {
    height: 100%;
  }

  .discogs-table {
    font-size: 13px !important;
  }

  .discogs-table>>>th {
    padding: 0 8px !important;
    font-size: 12px !important;
  }

  .discogs-table>>>td {
    padding: 0 8px !important;
  }

  .album-cover {
    width: 40px !important;
    height: 40px !important;
  }
}
</style>